import { formatCallDuration } from '@common/helpers/arcVoice'
import { sleep } from '@common/helpers/helpers'
import {
  ARC_VOICE_GO_TO_TOOL_NAME,
  ARC_VOICE_BRIEF_TOOL_NAME,
  ARC_VOICE_CANCEL_TOOL_NAME,
  ARC_VOICE_HEARTBEAT_MS,
  ARC_VOICE_PARK_TOOL_NAME,
  ARC_VOICE_RESUME_WINDOW_MS,
  ARC_VOICE_TOOL_NAME,
  ARC_VOICE_TRANSCRIPT_LINE_MAX_CHARS,
  ARC_VOICE_TRANSCRIPT_MAX_LINES,
  ArcVoiceAnswerStatus,
  ArcVoiceCallRecordStatus,
  ArcVoiceSpeaker,
  IArcVoiceAnswer,
  IArcVoiceCallCheckpoint,
  IArcVoiceCallLine,
  IArcVoiceSession,
} from '@common/interfaces/arcVoice'
import { ApprovalStatus, AtlasAIMessageStatus } from '@common/interfaces/atlasai'
import groupBy from 'lodash/groupBy'
import { IArcApprovalResolution } from '../arcPresence/arcPresenceStore'
import {
  toCallAgendaEntries,
  parkedItems,
  comparable,
  runningDuplicate,
  parkedNudgePrompt,
  backgroundItem,
  addAgendaItem,
  agendaBriefing,
  agendaProgress,
  agendaTitle,
  answerPrompt,
  ArcVoiceAgendaStatus,
  cancelNote,
  cancelTarget,
  isAwaitingResult,
  itemTurnIds,
  CancelTargetKind,
  ArcVoiceDelivery,
  deliveryFor,
  IAgendaItemEffects,
  IArcVoiceAgendaItem,
  isOnAgenda,
  ICancelOutcome,
  narrationPrompt,
  nothingToCancelNote,
  offerPrompt,
  readyToTell,
  resultNote,
  settleAgendaItem,
  stopNotice,
  updateAgendaItem,
  workingItems,
} from './callAgenda'
import { emptyTally, IEchoGateTally, mergeTallies, summarizeTally } from './echoGate'
import {
  ArcVoiceActivity,
  IArcVoiceCaption,
  IArcVoiceLevels,
  IRealtimeVoiceHandlers,
} from './realtimeVoiceClient'

const IDLE_CHECK_INTERVAL_MS = 5 * 1000

/** At most one spoken progress update this often, so Arc working never becomes chatter. */
const NARRATION_GAP_MS = 12 * 1000

/** What an item waiting on an approval becomes when nothing carries it on. */
const APPROVAL_SETTLED_RESULTS: Partial<
  Record<ApprovalStatus, Pick<IArcVoiceAgendaItem, 'outcome' | 'result'>>
> = {
  [ApprovalStatus.Executed]: {
    outcome: ArcVoiceAnswerStatus.Answered,
    result: 'They approved it and it ran.',
  },
  [ApprovalStatus.Rejected]: {
    outcome: ArcVoiceAnswerStatus.Answered,
    result:
      'They turned down the step that was waiting on their approval, so that step did not run. Anything done before it still stands.',
  },
  [ApprovalStatus.Failed]: {
    outcome: ArcVoiceAnswerStatus.Failed,
    result: 'They approved it, but it failed when it ran; the reason is in the chat.',
  },
}

/** A parked item is brought back after this much quiet: a real lull, not a breath. */
const PARKED_NUDGE_QUIET_MS = 15 * 1000
/** And no more often than this, so a quiet stretch isn't filled with reminders. */
const PARKED_NUDGE_GAP_MS = 2 * 60 * 1000

/**
 * A result waits for this much quiet before it is brought up, so it lands in a
 * real pause instead of the first breath after Arc's own sentence.
 */
const DELIVERY_GAP_MS = 1800

/** A request is followed through a backend restart: about a minute and a half of failed polls. */
const ANSWER_POLL_MAX_FAILURES = 15
const ANSWER_POLL_MAX_BACKOFF_MS = 8 * 1000

/** What the voice is told when it lost track of a request, rather than that the request failed. */
export const LOST_TRACK_ANSWER =
  'I lost the connection while this ran, so I don’t know how it went; whatever it finished is in the chat.'

/** A new caption this soon after the caller's last line last grew, carrying it on, is the same line. */
const CAPTION_MERGE_WINDOW_MS = 20 * 1000

/** Words two captions open with in common, so a reworded tail ("wanna" → "want to") still matches. */
const SHARED_OPENING_MIN_WORDS = 4
const SHARED_OPENING_MIN_SHARE = 0.5

/** The new caption carries on the earlier one: it opens with most of what the earlier one said. */
export const continuesCaption = (earlier: string, next: string): boolean => {
  const before = comparable(earlier)
  const after = comparable(next)
  if (after.startsWith(before)) return true
  const beforeWords = before.split(' ')
  const afterWords = after.split(' ')
  let shared = 0
  while (shared < beforeWords.length && beforeWords[shared] === afterWords[shared]) shared += 1
  return (
    shared >= SHARED_OPENING_MIN_WORDS &&
    shared / beforeWords.length >= SHARED_OPENING_MIN_SHARE &&
    afterWords.length >= beforeWords.length
  )
}

/** Provider warnings the session survives are shown at most this often. */
const WARNING_GAP_MS = 30 * 1000

/** Room for the transcript and agenda in an unload beacon, under the browser's 64KB keepalive cap. */
const UNLOAD_PROGRESS_MAX_CHARS = 56 * 1024

/** How long after the last finished line the record is checkpointed. */
const CHECKPOINT_DELAY_MS = 8 * 1000

/** A dropped line is redialled this many times, a little later each time, before the call pauses. */
const RECONNECT_MAX_ATTEMPTS = 3
const RECONNECT_DELAY_MS = 1000

export enum ArcVoiceCallStatus {
  Connecting = 'connecting',
  Live = 'live',
  Paused = 'paused',
  Ended = 'ended',
}

export enum ArcVoicePauseReason {
  User = 'user',
  Idle = 'idle',
  Limit = 'limit',
  Disconnected = 'disconnected',
}

export interface IArcVoiceTranscriptLine {
  id: string
  speaker: ArcVoiceSpeaker
  text: string
  /** Epoch ms the line started. */
  at: number
  isFinal: boolean
}

export interface IArcVoiceCallSnapshot {
  /** Null until the conversation a new call is starting is created. */
  channelId: string | null
  status: ArcVoiceCallStatus
  activity: ArcVoiceActivity | null
  /** Requests handed to Arc that haven't been answered yet. */
  isArcWorking: boolean
  /** Their mic is off: nothing they say reaches the voice, though it still talks and works. */
  isMuted: boolean
  /** Arc's latest status line while it works. */
  progress: string | null
  /** Everything asked on the call, oldest first, and where each stands. */
  agenda: IArcVoiceAgendaItem[]
  lines: IArcVoiceTranscriptLine[]
  startedAt: number
  /** Talk time banked before the current live stretch. */
  activeMs: number
  /** When the current live stretch began; null while not live. */
  liveSince: number | null
  pauseReason: ArcVoicePauseReason | null
  /** The call's row in the DM, once it has one. */
  recordId: string | null
}

/** Talk time so far, pauses excluded. */
export const talkTimeMs = (snapshot: IArcVoiceCallSnapshot, now: number): number =>
  snapshot.activeMs + (snapshot.liveSince ? now - snapshot.liveSince : 0)

/** The parts of RealtimeVoiceClient the call drives, so tests can stand in for it. */
export interface IArcVoiceConnection {
  readonly isBusy: boolean
  readonly levels: IArcVoiceLevels
  /** `isRedial`: opened without a click, so it fails rather than connect with no sound. */
  connect(
    session: IArcVoiceSession,
    mic: MediaStream,
    conversationId: string | null,
    isRedial?: boolean,
  ): Promise<void>
  prompt(instructions: string): void
  /** Add a note to the voice's conversation, which it keeps for the rest of the call. */
  addContext(text: string): void
  setThinkingCue(isOn: boolean): void
  setPageContext(pageContext: string | null): void
  /** The text of what is on their screen; part of its standing brief. */
  setScreenText(screenText: string | null): void
  /** What the voice keeps in mind of the call's agenda; part of its standing brief. */
  setAgenda(briefing: string | null): void
  /** Keep the mic from the provider, dropping whatever it was still holding of it. */
  setMuted(isMuted: boolean): void
  /** What the mic heard while Arc talked on this connection. */
  readonly audioTally?: IEchoGateTally
  hangUp(): void
}

/** How one turn ended when a stop reached it. */
export interface IStoppedTurn {
  id: string
  status: AtlasAIMessageStatus
}

const FINISHED_TURN_STATUSES = new Set<AtlasAIMessageStatus | undefined>([
  AtlasAIMessageStatus.COMPLETED,
  AtlasAIMessageStatus.COMPLETED_WITH_ISSUES,
])
const SETTLED_TURN_STATUSES = new Set<AtlasAIMessageStatus | undefined>([
  ...FINISHED_TURN_STATUSES,
  AtlasAIMessageStatus.CANCELLED,
  AtlasAIMessageStatus.FAILED,
])

export interface IArcVoiceCallDeps {
  createConnection: (handlers: IRealtimeVoiceHandlers) => IArcVoiceConnection
  getMicrophone: () => Promise<MediaStream>
  /** `rejoinCallId`: the earlier call row this call picks up, for the voice to know what was said. */
  startSession: (channelId: string, rejoinCallId: string | null) => Promise<IArcVoiceSession>
  ask: (channelId: string, request: string, callRecordId: string | null) => Promise<IArcVoiceAnswer>
  getAnswer: (
    channelId: string,
    chatHistoryId: string,
    knownProgress: string | null,
  ) => Promise<IArcVoiceAnswer>
  createRecord: (
    channelId: string,
    record: {
      startedAt: string
      durationMs: number
      transcript: IArcVoiceCallLine[]
      rejoinCallId?: string
    },
  ) => Promise<{ id: string }>
  /**
   * `isUnloading` asks for a request that outlives the page. Resolves to the row's
   * status after the write, or null when it can't tell (an unload beacon).
   */
  checkpoint: (
    channelId: string,
    recordId: string,
    checkpoint: IArcVoiceCallCheckpoint,
    isUnloading: boolean,
  ) => Promise<ArcVoiceCallRecordStatus | null>
  notify: (message: string, isError: boolean) => void
  now?: () => number
  /** How much quiet a result waits for; tests shorten it. */
  deliveryGapMs?: number
  /** How often a held call checkpoints its row to show it is still held; tests shorten it. */
  heartbeatMs?: number
  /** How often the line is checked for quiet; tests shorten it. */
  idleCheckIntervalMs?: number
  /** The voice's go_to tool: move the screen to a page or record, in the browser. */
  goTo?: (args: Record<string, unknown>) => Promise<unknown>
  /** Stop turns of one Arc session; resolves to how each one ended. */
  stopRuns?: (sessionId: string, turnIds: string[]) => Promise<IStoppedTurn[]>
  /** What an Arc turn did on screen, so a result they already saw isn't said again. */
  turnEffects?: (chatHistoryId: string) => IAgendaItemEffects
}

const FAILED_TOOL_RESULT = { status: ArcVoiceAnswerStatus.Failed }

/** Enough for a call's aims and ground rules, without growing every request without end. */
const CALL_BRIEF_MAX_NOTES = 6

const workingToolResult = (number: number) => ({
  status: ArcVoiceAnswerStatus.Working,
  agendaNumber: number,
  note: `Started, as #${number} on your agenda. Say in a few words what you're doing and keep talking; you'll be told the result.`,
})

/** A call's lines as its record keeps them: trimmed, capped, and none left blank. */
export const toRecordTranscript = (
  lines: Pick<IArcVoiceTranscriptLine, 'speaker' | 'text' | 'at'>[],
): IArcVoiceCallLine[] =>
  lines
    .filter(({ text }) => text.trim())
    .map(({ speaker, text, at }) => ({
      speaker,
      text: text.trim().slice(0, ARC_VOICE_TRANSCRIPT_LINE_MAX_CHARS),
      at: new Date(at).toISOString(),
    }))

/**
 * One voice call with Arc, across however many connections it takes: pausing
 * closes the line and resuming reopens it on the same provider conversation.
 * The voice model holds the conversation; each request it hands off goes to Arc
 * through `ask`, and is then followed in the background so the call keeps
 * talking while Arc works. The answer is read out when there's a gap.
 */
export class ArcVoiceCall {
  private snapshot: IArcVoiceCallSnapshot

  private readonly listeners = new Set<() => void>()

  private connection: IArcVoiceConnection | null = null

  private session: IArcVoiceSession | null = null

  private conversationId: string | null = null

  private pausedAt = 0

  /** The latest page the caller moved to, carried onto each new connection. */
  private pageContext: string | null = null

  private screenText: string | null = null

  /** What the call is for and how to work on it, as they set it; sent with every request. */
  private brief: string[] = []

  /** Requests being handed to Arc right now, so the same one sent twice starts once. */
  private readonly requestsInFlight = new Set<string>()

  private lastActivityAt = 0

  private lastSpokeAt = 0

  private lastWarnedAt = 0

  /** Last time anyone spoke on the line, for telling a lull from Arc merely working. */
  private lastTalkAt = 0

  private lastNudgeAt = 0

  private timers: Array<ReturnType<typeof setTimeout>> = []

  private deliveryTimer: ReturnType<typeof setTimeout> | null = null

  /** What the mic heard while Arc talked, over every connection the call has had. */
  private audioTally: IEchoGateTally = emptyTally()

  private checkpointTimer: ReturnType<typeof setTimeout> | null = null

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null

  /** The earlier call row this one picks up, if it was rejoined from one. */
  private rejoinCallId: string | null = null

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null

  /** Redials since the line was last up; zero while it is. */
  private reconnectAttempts = 0

  private isCreatingRecord = false

  private readonly now: () => number

  /** Stops still on their way, by item, which a result for that item waits on. */
  private readonly pendingStops = new Map<string, Promise<ICancelOutcome>>()

  /** Each caption item's line, which several items may share. */
  private readonly captionLines = new Map<string, string>()

  /** When each line last grew: a long monologue keeps growing well past when it began. */
  private readonly lineUpdatedAt = new Map<string, number>()

  constructor(private readonly deps: IArcVoiceCallDeps) {
    this.now = deps.now ?? Date.now
    this.snapshot = {
      channelId: null,
      status: ArcVoiceCallStatus.Connecting,
      activity: null,
      isArcWorking: false,
      isMuted: false,
      progress: null,
      agenda: [],
      lines: [],
      startedAt: this.now(),
      activeMs: 0,
      liveSince: null,
      pauseReason: null,
      recordId: null,
    }
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  readonly getSnapshot = (): IArcVoiceCallSnapshot => this.snapshot

  get levels(): IArcVoiceLevels {
    return this.connection?.levels ?? { input: 0, output: 0 }
  }

  /**
   * Start the call. Call it inside the click that asked for it: the connection's
   * audio context is created before the first await. `channel` may still be
   * resolving, for a call that starts a new conversation. `rejoinCallId` is the
   * earlier call row this one picks up.
   */
  open(channel: string | Promise<string>, rejoinCallId: string | null = null): Promise<void> {
    this.rejoinCallId = rejoinCallId
    this.connection = this.createConnection()
    return this.connectInto(async () => {
      const channelId = await channel
      this.update({ channelId })
      return channelId
    }, null)
  }

  /** The caller moved to another page; the voice should know what they are looking at. */
  setPageContext(pageContext: string | null): void {
    this.pageContext = pageContext
    this.liveConnection()?.setPageContext(pageContext)
  }

  /** What is on their screen changed: "the third one" means what they can see now. */
  setScreenText(screenText: string | null): void {
    this.screenText = screenText
    this.liveConnection()?.setScreenText(screenText)
  }

  /** Their mic, on or off for the rest of the call; Arc's voice and results carry on either way. */
  setMuted(isMuted: boolean): void {
    if (isMuted === this.snapshot.isMuted || this.snapshot.status === ArcVoiceCallStatus.Ended) {
      return
    }
    // The quiet clock restarts: the shorter unmuted limit must not count the time spent on mute.
    this.lastActivityAt = this.now()
    this.update({ isMuted })
    this.connection?.setMuted(isMuted)
  }

  toggleMute(): void {
    this.setMuted(!this.snapshot.isMuted)
  }

  /** Stop pressed on screen: the one item, or everything running. Says on screen how it went. */
  async stopWork(itemId?: string): Promise<void> {
    const running = workingItems(this.snapshot.agenda)
    const items = itemId ? running.filter(({ id }) => id === itemId) : running
    if (items.length === 0) return
    const { message, isError } = stopNotice(await this.stopItems(items))
    this.deps.notify(message, isError)
  }

  /** Close the line; requests in flight keep running and are read out on resume. */
  pause(reason: ArcVoicePauseReason = ArcVoicePauseReason.User): void {
    const { status } = this.snapshot
    if (status !== ArcVoiceCallStatus.Live && status !== ArcVoiceCallStatus.Connecting) return
    this.reconnectAttempts = 0
    this.closeConnection()
    this.pausedAt = this.now()
    this.update({ status: ArcVoiceCallStatus.Paused, pauseReason: reason, ...this.bankTalkTime() })
    void this.checkpointNow(false)
  }

  /**
   * The page is going away (a reload, a closed tab). Send what was said, in a
   * request that outlives the page; the row stays open to be rejoined from, and
   * the server ends it if nobody does.
   */
  handlePageHide(): void {
    if (this.snapshot.status === ArcVoiceCallStatus.Ended) return
    void this.checkpointNow(true)
  }

  /** Reopen the line. Like `open`, call it inside the click that asked for it. */
  resume(): Promise<void> {
    if (this.snapshot.status !== ArcVoiceCallStatus.Paused || !this.snapshot.channelId) {
      return Promise.resolve()
    }
    const channelId = this.snapshot.channelId
    const canResume =
      !!this.conversationId && this.now() - this.pausedAt < ARC_VOICE_RESUME_WINDOW_MS
    this.connection = this.createConnection()
    this.update({ status: ArcVoiceCallStatus.Connecting, pauseReason: null })
    return this.connectInto(
      () => Promise.resolve(channelId),
      canResume ? this.conversationId : null,
    )
  }

  /** Hang up for good, and close the call's row in the DM. */
  async end(): Promise<void> {
    if (this.snapshot.status === ArcVoiceCallStatus.Ended) return
    const wasEverLive = this.snapshot.activeMs > 0 || this.snapshot.liveSince !== null
    this.hangUpHere()
    if (!wasEverLive) return

    const { activeMs } = this.snapshot
    this.deps.notify(`Arc Voice ended · ${formatCallDuration(activeMs)}`, false)
    await this.checkpointNow(false)
  }

  private async connectInto(
    resolveChannel: () => Promise<string>,
    conversationId: string | null,
    isRedial = false,
  ): Promise<void> {
    const connection = this.connection
    // Held here until the connection owns it: every way out before that releases it.
    let microphone: MediaStream | null = null
    const releaseMicrophone = () => microphone?.getTracks().forEach((track) => track.stop())
    try {
      microphone = await this.deps.getMicrophone()
      if (this.connection !== connection) {
        releaseMicrophone()
        return
      }
      const channelId = await resolveChannel()
      this.session = await this.deps.startSession(channelId, this.rejoinCallId)
      if (this.connection !== connection) {
        releaseMicrophone()
        return
      }
      const owned = microphone
      microphone = null
      await connection?.connect(this.session, owned, conversationId, isRedial)
    } catch (err) {
      releaseMicrophone()
      if (this.connection !== connection) return
      // A redial that runs out of tries says so once, in `redial`.
      if (!isRedial) {
        this.deps.notify((err as Error)?.message || 'Couldn’t connect Arc Voice.', true)
      }
      this.failConnecting()
    }
  }

  /** A first connection that fails ends the call; a failed resume leaves it paused. */
  private failConnecting(): void {
    if (this.reconnectAttempts > 0) {
      this.redial()
      return
    }
    const hasTalked = this.snapshot.activeMs > 0
    if (!hasTalked) {
      this.hangUpHere()
      return
    }
    this.closeConnection()
    this.update({
      status: ArcVoiceCallStatus.Paused,
      pauseReason: ArcVoicePauseReason.Disconnected,
    })
  }

  /** A new connection whose events are dropped once it is no longer the call's line. */
  private createConnection(): IArcVoiceConnection {
    let connection: IArcVoiceConnection | null = null
    const ifCurrent =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (connection && this.connection === connection) fn(...args)
      }

    connection = this.deps.createConnection({
      onReady: ifCurrent(() => this.handleReady()),
      onActivityChange: ifCurrent((activity: ArcVoiceActivity) => this.handleActivity(activity)),
      onCaption: ifCurrent((caption: IArcVoiceCaption) => this.handleCaption(caption)),
      onConversationId: ifCurrent((id: string) => {
        this.conversationId = id
      }),
      // A closed line's buffered tool call must not start work or move the screen.
      onToolCall: (name, args) =>
        connection &&
        this.connection === connection &&
        this.snapshot.status === ArcVoiceCallStatus.Live
          ? this.handleToolCall(name, args)
          : Promise.resolve(FAILED_TOOL_RESULT),
      onIdle: ifCurrent(() => this.scheduleDelivery()),
      onError: ifCurrent((message: string) => this.deps.notify(message, true)),
      // The session survives these, so the call carries on; shown, but at most this often.
      onWarning: ifCurrent((message: string) => {
        if (this.now() - this.lastWarnedAt < WARNING_GAP_MS) return
        this.lastWarnedAt = this.now()
        this.deps.notify(message, false)
      }),
      onDisconnect: ifCurrent(() => this.redial()),
    })
    return connection
  }

  /**
   * The line dropped: dial it again on the same conversation, so a blip costs a
   * second of silence instead of a click on Resume. After a few failed tries the
   * call pauses as before.
   */
  private redial(): void {
    const { channelId, status } = this.snapshot
    const isStillOn = status === ArcVoiceCallStatus.Live || status === ArcVoiceCallStatus.Connecting
    if (!channelId || !isStillOn || this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      this.reconnectAttempts = 0
      this.deps.notify('Lost the line to Arc. Resume to carry on.', false)
      this.pause(ArcVoicePauseReason.Disconnected)
      return
    }
    this.reconnectAttempts += 1
    this.closeConnection()
    // Connecting for a dropped line, which the status line reads as reconnecting.
    this.update({
      status: ArcVoiceCallStatus.Connecting,
      pauseReason: ArcVoicePauseReason.Disconnected,
      ...this.bankTalkTime(),
    })
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.snapshot.status !== ArcVoiceCallStatus.Connecting || this.connection) return
      this.connection = this.createConnection()
      void this.connectInto(() => Promise.resolve(channelId), this.conversationId, true)
    }, RECONNECT_DELAY_MS * this.reconnectAttempts)
  }

  private handleReady(): void {
    this.reconnectAttempts = 0
    const now = this.now()
    this.lastActivityAt = now
    this.lastTalkAt = now
    this.update({ status: ArcVoiceCallStatus.Live, liveSince: now, pauseReason: null })
    if (this.pageContext) this.connection?.setPageContext(this.pageContext)
    if (this.screenText) this.connection?.setScreenText(this.screenText)
    if (this.snapshot.isMuted) this.connection?.setMuted(true)
    this.connection?.setAgenda(agendaBriefing(this.snapshot.agenda))
    this.armTimers()
    this.syncThinkingCue()
    this.scheduleDelivery()
    void this.ensureRecord()
  }

  private handleActivity(activity: ArcVoiceActivity): void {
    this.lastActivityAt = this.now()
    if (activity !== ArcVoiceActivity.Listening) this.lastTalkAt = this.lastActivityAt
    // Somebody is talking: whatever was about to be brought up waits for the next pause.
    if (activity !== ArcVoiceActivity.Listening) this.cancelDelivery()
    if (activity === ArcVoiceActivity.Speaking) this.lastSpokeAt = this.lastActivityAt
    this.update({ activity })
  }

  /**
   * The line a caption belongs to. The provider sometimes splits one thing the
   * caller says into several items, each carrying everything said so far; those
   * fold into the line they carry on rather than stacking up as repeats.
   */
  private lineIdFor({ id, speaker, text }: IArcVoiceCaption): string {
    const known = this.captionLines.get(id)
    if (known) return known
    const last = this.snapshot.lines.at(-1)
    const continuesLast =
      speaker === ArcVoiceSpeaker.User &&
      last?.speaker === ArcVoiceSpeaker.User &&
      last.id !== id &&
      this.now() - (this.lineUpdatedAt.get(last.id) ?? last.at) < CAPTION_MERGE_WINDOW_MS &&
      continuesCaption(last.text, text)
    const lineId = continuesLast && last ? last.id : id
    this.captionLines.set(id, lineId)
    return lineId
  }

  private handleCaption(caption: IArcVoiceCaption): void {
    const { speaker, text, isFinal } = caption
    this.lastActivityAt = this.now()
    this.lastTalkAt = this.lastActivityAt
    const id = this.lineIdFor(caption)
    this.lineUpdatedAt.set(id, this.now())
    const lines = [...this.snapshot.lines]
    const index = lines.findIndex((line) => line.id === id)
    if (index >= 0) lines[index] = { ...lines[index], text, isFinal }
    else lines.push({ id, speaker, text, isFinal, at: this.now() })
    this.update({ lines: lines.slice(-ARC_VOICE_TRANSCRIPT_MAX_LINES) })
    if (isFinal) {
      this.scheduleCheckpoint()
    }
  }

  private async handleToolCall(name: string, args: Record<string, unknown>): Promise<unknown> {
    this.lastActivityAt = this.now()
    if (name === ARC_VOICE_PARK_TOOL_NAME) return this.parkItem(Number(args.number))
    if (name === ARC_VOICE_BRIEF_TOOL_NAME) return this.addToBrief(args.note)
    if (name === ARC_VOICE_CANCEL_TOOL_NAME) return this.cancelRequests(args)
    if (name === ARC_VOICE_GO_TO_TOOL_NAME) {
      try {
        return (await this.deps.goTo?.(args)) ?? FAILED_TOOL_RESULT
      } catch {
        return FAILED_TOOL_RESULT
      }
    }
    const { channelId } = this.snapshot
    if (name !== ARC_VOICE_TOOL_NAME || typeof args.request !== 'string' || !channelId) {
      return FAILED_TOOL_RESULT
    }
    return this.askArc(channelId, { ...args, request: args.request })
  }

  /**
   * The voice re-sent a request still running, or still being handed off: a second
   * turn would do the work twice, as two identical invites once did. A follow-up
   * changes a request on purpose, so it is never one.
   */
  private repeatOfRunning(args: { request: string; follows_up?: unknown }): unknown {
    const running =
      args.follows_up === undefined
        ? runningDuplicate(this.snapshot.agenda, args.request)
        : undefined
    if (!running && !this.requestsInFlight.has(comparable(args.request))) return null
    return {
      status: ArcVoiceAnswerStatus.Working,
      ...(running ? { agendaNumber: running.number } : {}),
      note: "That's already running. Don't send it again; say it's in progress.",
    }
  }

  /** Hand a request to Arc and follow it in the background, as an item on the agenda. */
  private async askArc(
    channelId: string,
    args: Record<string, unknown> & { request: string },
  ): Promise<unknown> {
    const repeat = this.repeatOfRunning(args)
    if (repeat) return repeat

    const key = comparable(args.request)
    let answer: IArcVoiceAnswer
    this.requestsInFlight.add(key)
    try {
      answer = await this.deps.ask(channelId, this.withBrief(args.request), this.snapshot.recordId)
    } catch {
      return FAILED_TOOL_RESULT
    } finally {
      this.requestsInFlight.delete(key)
    }
    if (answer.status !== ArcVoiceAnswerStatus.Working || !answer.chatHistoryId) {
      return FAILED_TOOL_RESULT
    }

    const chatHistoryId = answer.chatHistoryId
    this.setAgenda(
      addAgendaItem(this.snapshot.agenda, {
        id: chatHistoryId,
        title: agendaTitle(args.title, args.request),
        request: args.request,
        followsUp: args.follows_up,
        sessionId: answer.sessionId ?? null,
        at: this.now(),
      }),
    )
    void this.follow(channelId, chatHistoryId)
    return workingToolResult(this.agendaItem(chatHistoryId)?.number ?? 0)
  }

  /** An objective or way of working they set for the call; every later request carries it. */
  private addToBrief(note: unknown): unknown {
    const text = typeof note === 'string' ? note.trim() : ''
    if (!text) return FAILED_TOOL_RESULT
    if (!this.brief.includes(text)) this.brief = [...this.brief, text].slice(-CALL_BRIEF_MAX_NOTES)
    return { noted: true, note: 'Noted for the rest of the call. Acknowledge it in a few words.' }
  }

  /** Stop what they took back, with the conversation's own Stop rather than another Arc turn. */
  private async cancelRequests(args: Record<string, unknown>): Promise<unknown> {
    const target = cancelTarget(this.snapshot.agenda, {
      number: typeof args.number === 'number' ? args.number : undefined,
      target: typeof args.target === 'string' ? args.target : undefined,
      isAll: args.all === true,
    })
    if (target.kind !== CancelTargetKind.Stop) {
      return { stopped: [], note: nothingToCancelNote(target) }
    }
    const outcome = await this.stopItems(target.items)
    return { stopped: outcome.stopped.map(({ number }) => number), note: cancelNote(outcome) }
  }

  /** A result that lands while its item is being stopped waits on this (see `follow`). */
  private stopItems(items: IArcVoiceAgendaItem[]): Promise<ICancelOutcome> {
    const stopping = this.stopTurns(items)
    items.forEach(({ id }) => this.pendingStops.set(id, stopping))
    return stopping.finally(() => items.forEach(({ id }) => this.pendingStops.delete(id)))
  }

  /** One stop per session, so the conversation is told once however many it stops. */
  private async stopTurns(items: IArcVoiceAgendaItem[]): Promise<ICancelOutcome> {
    const turnOf = (item: IArcVoiceAgendaItem) => item.turnId ?? item.id
    const ended = new Map<string, AtlasAIMessageStatus>()
    const failed = new Set<string>()
    await Promise.all(
      Object.entries(groupBy(items, ({ sessionId }) => sessionId ?? '')).map(
        async ([sessionId, group]) => {
          const turnIds = group.map(turnOf)
          try {
            // Saved before calls kept the session: nothing to key a stop on.
            if (!sessionId || !this.deps.stopRuns) throw new Error('No session to stop')
            const allTurnIds = [...new Set(group.flatMap(itemTurnIds))]
            ;(await this.deps.stopRuns(sessionId, allTurnIds)).forEach(({ id, status }) =>
              ended.set(id, status),
            )
          } catch {
            turnIds.forEach((id) => failed.add(id))
          }
        },
      ),
    )
    const endOf = (item: IArcVoiceAgendaItem) =>
      failed.has(turnOf(item)) ? undefined : ended.get(turnOf(item))
    const outcome: ICancelOutcome = {
      stopped: items.filter((item) => endOf(item) === AtlasAIMessageStatus.CANCELLED),
      finished: items.filter((item) => FINISHED_TURN_STATUSES.has(endOf(item))),
      hadFailed: items.filter((item) => endOf(item) === AtlasAIMessageStatus.FAILED),
      // Not reached, or still live after the stop: it may still be running.
      failed: items.filter((item) => !SETTLED_TURN_STATUSES.has(endOf(item))),
    }
    const at = this.now()
    this.setAgenda(
      outcome.stopped.reduce(
        (agenda, { id }) =>
          updateAgendaItem(agenda, id, {
            status: ArcVoiceAgendaStatus.Stopped,
            progress: null,
            updatedAt: at,
          }),
        this.snapshot.agenda,
      ),
    )
    return outcome
  }

  private withBrief(request: string): string {
    if (this.brief.length === 0) return request
    return `${request}\n\nFor this call, they've asked:\n${this.brief.map((note) => `- ${note}`).join('\n')}`
  }

  private agendaItem(id: string): IArcVoiceAgendaItem | undefined {
    return this.snapshot.agenda.find((item) => item.id === id)
  }

  /** Still following this turn for this item: an item can move on to a later turn. */
  private isFollowing(itemId: string, turnId: string): boolean {
    const item = this.agendaItem(itemId)
    return (
      this.snapshot.status !== ArcVoiceCallStatus.Ended &&
      !!item &&
      isAwaitingResult(item) &&
      (item.turnId ?? item.id) === turnId
    )
  }

  /** Long-poll one turn until it settles, narrating its progress on the item along the way. */
  private async follow(
    channelId: string,
    chatHistoryId: string,
    itemId = chatHistoryId,
  ): Promise<void> {
    let knownProgress: string | null = null
    let failures = 0
    while (this.isFollowing(itemId, chatHistoryId)) {
      let answer: IArcVoiceAnswer
      try {
        answer = await this.deps.getAnswer(channelId, chatHistoryId, knownProgress)
        failures = 0
      } catch {
        failures += 1
        if (failures < ANSWER_POLL_MAX_FAILURES) {
          await sleep(Math.min(1000 * 2 ** (failures - 1), ANSWER_POLL_MAX_BACKOFF_MS))
          continue
        }
        answer = {
          status: ArcVoiceAnswerStatus.Failed,
          spokenAnswer: LOST_TRACK_ANSWER,
          draft: null,
          isDraftCut: false,
          chatHistoryId,
          progress: null,
        }
      }
      if (!this.isFollowing(itemId, chatHistoryId)) return

      if (answer.status === ArcVoiceAnswerStatus.Working) {
        if (answer.progress && answer.progress !== knownProgress) {
          knownProgress = answer.progress
          this.setAgenda(
            updateAgendaItem(this.snapshot.agenda, itemId, {
              progress: knownProgress,
              updatedAt: this.now(),
            }),
          )
          if (this.agendaItem(itemId)?.status === ArcVoiceAgendaStatus.Working) this.narrate(itemId)
        }
        continue
      }

      // A stop on its way decides how this ends: as stopped, or as it came back.
      await this.pendingStops.get(itemId)
      if (!this.isFollowing(itemId, chatHistoryId)) return
      this.setAgenda(
        settleAgendaItem(this.snapshot.agenda, { ...answer, chatHistoryId: itemId }, this.now()),
      )
      this.keepSetAsideResult(itemId)
      this.scheduleDelivery()
    }
  }

  /** A set-aside item isn't told at a pause: its result goes to the voice unsaid, for when they ask. */
  private keepSetAsideResult(itemId: string): void {
    const item = this.agendaItem(itemId)
    if (!item || item.status === ArcVoiceAgendaStatus.Ready) return
    this.liveConnection()?.addContext(resultNote(item))
  }

  /** Brought into a huddle: several people talking, so Arc speaks up rather than waits for quiet. */
  private get isRoom(): boolean {
    return !!this.session?.isRoom
  }

  private narrate(chatHistoryId: string): void {
    // A room hears the answer, not the steps: progress there is just Arc talking over people.
    if (this.isRoom) return
    const connection = this.liveConnection()
    const item = this.agendaItem(chatHistoryId)
    if (!connection || connection.isBusy || !item?.progress) return
    if (this.now() - this.lastSpokeAt < NARRATION_GAP_MS) return
    this.lastSpokeAt = this.now()
    connection.prompt(narrationPrompt(item, workingItems(this.snapshot.agenda).length === 1))
  }

  /**
   * An approval card was answered. One an item was waiting on puts it back to
   * work on the turn Arc resumed with, so the call hears how the rest went; a
   * turned-down or failed one settles the item so it is said.
   */
  handleApprovalResolved({
    status,
    atlasChatHistoryId,
    continuationChatHistoryId,
  }: IArcApprovalResolution): void {
    const { channelId, agenda } = this.snapshot
    const item = agenda.find(
      ({ id, turnId, outcome }) =>
        outcome === ArcVoiceAnswerStatus.AwaitingApproval && (turnId ?? id) === atlasChatHistoryId,
    )
    if (!item || !channelId) return

    if (status === ApprovalStatus.Executed && continuationChatHistoryId) {
      this.setAgenda(
        updateAgendaItem(agenda, item.id, {
          status: ArcVoiceAgendaStatus.Working,
          outcome: null,
          result: null,
          draft: null,
          isDraftCut: false,
          progress: 'Carrying on after your approval',
          turnId: continuationChatHistoryId,
          earlierTurnIds: item.turnId ? [...(item.earlierTurnIds ?? []), item.turnId] : [],
          updatedAt: this.now(),
        }),
      )
      void this.follow(channelId, continuationChatHistoryId, item.id)
      return
    }

    const settled = APPROVAL_SETTLED_RESULTS[status]
    if (!settled) return
    this.setAgenda(
      updateAgendaItem(agenda, item.id, {
        status: ArcVoiceAgendaStatus.Ready,
        ...settled,
        draft: null,
        isDraftCut: false,
        updatedAt: this.now(),
      }),
    )
    this.scheduleDelivery()
  }

  /**
   * An Arc reply posted into the call's conversation. One that answers nothing on
   * the agenda came from work Arc ran in the background (a subagent finishing,
   * say) and would otherwise never be heard: it joins the agenda, to be mentioned
   * at the next pause.
   */
  handleArcReply({
    channelId,
    arcTurnId,
    voiceCallMessageId,
    content,
  }: {
    channelId: string
    arcTurnId: string | null
    voiceCallMessageId?: string | null
    content: string
  }): void {
    const { status, agenda, recordId } = this.snapshot
    const isThisCall =
      status !== ArcVoiceCallStatus.Ended &&
      channelId === this.snapshot.channelId &&
      !!recordId &&
      voiceCallMessageId === recordId
    if (!isThisCall || !arcTurnId || !content.trim()) return
    if (isOnAgenda(agenda, arcTurnId)) return
    this.setAgenda(backgroundItem(agenda, { turnId: arcTurnId, reply: content, at: this.now() }))
    this.scheduleDelivery()
  }

  /** Set an item aside, at their word, to bring back at a lull or before they go. */
  private parkItem(number: number): unknown {
    const item = this.snapshot.agenda.find((entry) => entry.number === number)
    if (!item) return { parked: false, note: `Nothing is #${number} on your agenda.` }
    // Its result, if it has one, is already in the voice's conversation once offered.
    if (item.status === ArcVoiceAgendaStatus.Ready)
      this.liveConnection()?.addContext(resultNote(item))
    this.setAgendaStatus(item.id, ArcVoiceAgendaStatus.Parked)
    return { parked: true, note: `Parked #${number}. Say you'll come back to it, in a few words.` }
  }

  /** At a real lull, with nothing else to say, bring one parked item back. */
  private nudgeParked(): void {
    const connection = this.liveConnection()
    const [item] = parkedItems(this.snapshot.agenda)
    if (!connection || connection.isBusy || !item) return
    if (readyToTell(this.snapshot.agenda).length > 0) return
    const now = this.now()
    if (now - this.lastTalkAt < PARKED_NUDGE_QUIET_MS) return
    if (now - this.lastNudgeAt < PARKED_NUDGE_GAP_MS) return
    this.lastNudgeAt = now
    this.lastSpokeAt = now
    connection.prompt(parkedNudgePrompt(item))
    this.setAgendaStatus(item.id, ArcVoiceAgendaStatus.Offered)
  }

  /** Say the next ready result now, rather than at the next pause. */
  tellNow(): void {
    this.cancelDelivery()
    this.deliverNext()
  }

  /** Bring the next settled result up once the line has been quiet for a moment. */
  private scheduleDelivery(): void {
    this.cancelDelivery()
    if (readyToTell(this.snapshot.agenda).length === 0) return
    this.deliveryTimer = setTimeout(() => {
      this.deliveryTimer = null
      this.deliverNext()
    }, this.deps.deliveryGapMs ?? DELIVERY_GAP_MS)
  }

  private cancelDelivery(): void {
    if (this.deliveryTimer) clearTimeout(this.deliveryTimer)
    this.deliveryTimer = null
  }

  /**
   * One result per pause: told outright when it is what they're waiting on,
   * otherwise mentioned with a question, so a result from earlier never takes
   * over the conversation they have moved on to. The result goes into the voice's
   * conversation either way, ready for when they want it.
   */
  private deliverNext(): void {
    const connection = this.liveConnection()
    if (!connection || connection.isBusy) return
    for (const item of readyToTell(this.snapshot.agenda)) {
      const usual = deliveryFor(
        item,
        this.snapshot.agenda,
        this.deps.turnEffects?.(item.turnId ?? item.id),
      )
      // In a huddle someone asked out loud and is waiting: tell it, don't offer it.
      const delivery =
        this.isRoom && usual === ArcVoiceDelivery.Offer ? ArcVoiceDelivery.Tell : usual
      connection.addContext(resultNote(item))
      this.setAgendaStatus(
        item.id,
        delivery === ArcVoiceDelivery.Offer
          ? ArcVoiceAgendaStatus.Offered
          : ArcVoiceAgendaStatus.Told,
      )
      if (delivery === ArcVoiceDelivery.Silent) continue

      this.lastSpokeAt = this.now()
      const isOtherWorkOpen = workingItems(this.snapshot.agenda).length > 0
      connection.prompt(
        delivery === ArcVoiceDelivery.Tell
          ? answerPrompt(item, isOtherWorkOpen)
          : offerPrompt(item),
      )
      break
    }
  }

  private setAgendaStatus(id: string, status: ArcVoiceAgendaStatus): void {
    this.setAgenda(updateAgendaItem(this.snapshot.agenda, id, { status, updatedAt: this.now() }))
  }

  private liveConnection(): IArcVoiceConnection | null {
    return this.snapshot.status === ArcVoiceCallStatus.Live ? this.connection : null
  }

  /** The agenda changed: the screen, the working cue and the voice's brief all follow it. */
  private setAgenda(agenda: IArcVoiceAgendaItem[]): void {
    this.update({
      agenda,
      isArcWorking: workingItems(agenda).length > 0,
      progress: agendaProgress(agenda),
    })
    this.syncThinkingCue()
    this.liveConnection()?.setAgenda(agendaBriefing(agenda))
  }

  private syncThinkingCue(): void {
    this.liveConnection()?.setThinkingCue(this.snapshot.isArcWorking)
  }

  /** Silence pauses the call, and a connection pauses at its cap; both resume on a fresh line. */
  private armTimers(): void {
    this.clearTimers()
    const session = this.session
    if (!session) return
    const idleCheck = setInterval(() => {
      this.nudgeParked()
      if (this.connection?.isBusy || this.snapshot.isArcWorking) this.lastActivityAt = this.now()
      const { isMuted } = this.snapshot
      const quietLimitMs = isMuted ? session.mutedIdleTimeoutMs : session.idleTimeoutMs
      if (this.now() - this.lastActivityAt < quietLimitMs) return
      this.deps.notify(
        `Paused Arc Voice after ${isMuted ? 'a few quiet minutes on mute' : 'a minute of quiet'}. Resume whenever you’re ready.`,
        false,
      )
      this.pause(ArcVoicePauseReason.Idle)
    }, this.deps.idleCheckIntervalMs ?? IDLE_CHECK_INTERVAL_MS)
    const cap = setTimeout(() => {
      this.deps.notify('Paused Arc Voice: resume to keep going on a fresh line.', false)
      this.pause(ArcVoicePauseReason.Limit)
    }, session.maxSessionMs)
    this.timers = [idleCheck, cap]
  }

  private clearTimers(): void {
    this.timers.forEach((timer) => clearTimeout(timer))
    this.timers = []
  }

  private closeConnection(): void {
    this.clearTimers()
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.cancelDelivery()
    if (this.connection?.audioTally) {
      this.audioTally = mergeTallies(this.audioTally, this.connection.audioTally)
    }
    this.connection?.hangUp()
    this.connection = null
    this.update({ activity: null })
  }

  /** The call ends in this browser; what its row says is up to whoever ended it. */
  private hangUpHere(): void {
    this.closeConnection()
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = null
    this.update({ status: ArcVoiceCallStatus.Ended, ...this.bankTalkTime() })
  }

  /**
   * Its row was ended elsewhere: rejoined from another tab or device, or swept as
   * abandoned while this tab was asleep or frozen, which it can't tell apart.
   */
  private handleEndedElsewhere(): void {
    this.hangUpHere()
    this.deps.notify(
      'This Arc Voice call has ended. Rejoin it from the conversation to carry on.',
      false,
    )
  }

  private bankTalkTime(): Pick<IArcVoiceCallSnapshot, 'activeMs' | 'liveSince'> {
    return { activeMs: talkTimeMs(this.snapshot, this.now()), liveSince: null }
  }

  private recordTranscript(): IArcVoiceCallLine[] {
    return toRecordTranscript(this.snapshot.lines)
  }

  /** The call's row in the DM, written the first time the line goes live. */
  private async ensureRecord(): Promise<void> {
    const { channelId, recordId, startedAt } = this.snapshot
    if (!channelId || recordId || this.isCreatingRecord) return
    this.isCreatingRecord = true
    try {
      const { id } = await this.deps.createRecord(channelId, {
        startedAt: new Date(startedAt).toISOString(),
        durationMs: 0,
        transcript: this.recordTranscript(),
        ...(this.rejoinCallId ? { rejoinCallId: this.rejoinCallId } : {}),
      })
      this.update({ recordId: id })
      // Hung up before the row came back: close it now.
      if (this.snapshot.status === ArcVoiceCallStatus.Ended) await this.checkpointNow(false)
      else this.startHeartbeat()
    } catch {
      this.deps.notify('Couldn’t save this Arc Voice call to the conversation.', true)
    } finally {
      this.isCreatingRecord = false
    }
  }

  /** Paused or live, a call held here keeps its row from being swept as abandoned. */
  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(
      () => void this.checkpointNow(false),
      this.deps.heartbeatMs ?? ARC_VOICE_HEARTBEAT_MS,
    )
  }

  private scheduleCheckpoint(): void {
    if (this.checkpointTimer) return
    this.checkpointTimer = setTimeout(() => {
      this.checkpointTimer = null
      void this.checkpointNow(false)
    }, CHECKPOINT_DELAY_MS)
  }

  private async checkpointNow(isUnloading: boolean): Promise<void> {
    if (this.checkpointTimer) clearTimeout(this.checkpointTimer)
    this.checkpointTimer = null
    const { channelId, recordId, status } = this.snapshot
    if (!channelId || !recordId) return
    const isEnded = status === ArcVoiceCallStatus.Ended
    const progress = {
      transcript: this.recordTranscript(),
      agenda: toCallAgendaEntries(this.snapshot.agenda),
    }
    // A keepalive request over 64KB is refused outright. Leave the transcript and
    // agenda out of one that wouldn't fit: the row keeps its last copy of both.
    const canCarryProgress =
      !isUnloading || JSON.stringify(progress).length <= UNLOAD_PROGRESS_MAX_CHARS
    try {
      const recordStatus = await this.deps.checkpoint(
        channelId,
        recordId,
        {
          status: isEnded ? ArcVoiceCallRecordStatus.Ended : ArcVoiceCallRecordStatus.Live,
          durationMs: Math.round(talkTimeMs(this.snapshot, this.now())),
          ...(canCarryProgress ? progress : {}),
          ...(isEnded
            ? {
                endedAt: new Date(this.now()).toISOString(),
                ...(this.session
                  ? { audioStats: summarizeTally(this.audioTally, this.session.cutInLevel) }
                  : {}),
              }
            : {}),
        },
        isUnloading,
      )
      const isStillHeldHere = this.snapshot.status !== ArcVoiceCallStatus.Ended
      if (recordStatus === ArcVoiceCallRecordStatus.Ended && isStillHeldHere) {
        this.handleEndedElsewhere()
      }
    } catch {
      if (isEnded) this.deps.notify('Couldn’t save the end of this Arc Voice call.', true)
    }
  }

  private update(patch: Partial<IArcVoiceCallSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch }
    this.listeners.forEach((listener) => listener())
  }
}
